"use client";

import { clearAuthCookiesForSignIn } from "@/actions/clear-auth-cookies";

// Keep in sync with cookies.server.ts (client bundle cannot import that module)
export const AUTHJS_SESSION_JWT_COOKIE_NAMES = [
  "authjs.session-token",
  "__Secure-authjs.session-token",
] as const;

/** Keep in sync with cookies.server expandSessionJwtCookieNames */
function expandSessionJwtCookieNamesClient(
  names: readonly string[] = AUTHJS_SESSION_JWT_COOKIE_NAMES,
): string[] {
  const expanded = new Set<string>();
  for (const name of names) {
    expanded.add(name);
    if (
      (AUTHJS_SESSION_JWT_COOKIE_NAMES as readonly string[]).includes(name)
    ) {
      for (let i = 0; i < 6; i++) {
        expanded.add(`${name}.${i}`);
      }
    }
  }
  return [...expanded];
}

export const AUTHJS_SESSION_COOKIE_NAMES = [
  "authjs.callback-url",
  "__Secure-authjs.callback-url",
  ...AUTHJS_SESSION_JWT_COOKIE_NAMES,
  "authjs.csrf-token",
  "__Host-authjs.csrf-token",
] as const;

const AUTH_COOKIE_NAMES = [
  "_tact",
  "oauth-net-bridge",
  "credentials-net-bridge",
  "credentials-net-at",
  "credentials-net-rt",
  "selected-org-id",
  ...AUTHJS_SESSION_COOKIE_NAMES,
  "net.session-token",
  "net.refresh-token",
] as const;

/** Full clear except CSRF — Auth.js signIn/signOut require the CSRF cookie. */
const AUTH_COOKIE_NAMES_WITHOUT_CSRF = AUTH_COOKIE_NAMES.filter(
  (name) => !name.includes("csrf"),
);

export type CookieName = string;

const SETTLE_MS = 200;

async function clearAuthCookiesViaApi(names: readonly string[]): Promise<void> {
  try {
    await fetch("/api/auth/clear", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names: [...names] }),
    });
  } catch {
    await clearAuthCookiesForSignIn(names);
  }
}

function getDomainVariants(hostname: string): string[] {
  const parts = hostname.split(".");
  const variants: string[] = [];

  for (let i = 0; i <= parts.length - 2; i++) {
    const domain = parts.slice(i).join(".");
    variants.push(domain, `.${domain}`);
  }

  return Array.from(new Set(variants));
}

function getPathVariants(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  const variants: string[] = ["/"];

  for (let i = segments.length; i > 0; i--) {
    const path = "/" + segments.slice(0, i).join("/");
    variants.push(path);
  }

  return Array.from(new Set(variants));
}

function buildDeletionStrings(
  name: string,
  path: string | undefined,
  domain: string | undefined,
  useNoneSameSite: boolean,
): string {
  const isHttps =
    typeof window !== "undefined" && window.location.protocol === "https:";
  const attributes: string[] = [
    `${name}=`,
    "Expires=Thu, 01 Jan 1970 00:00:00 GMT",
    "Max-Age=0",
  ];

  if (path) attributes.push(`Path=${path}`);
  if (domain) attributes.push(`Domain=${domain}`);

  if (useNoneSameSite) {
    attributes.push("SameSite=None");
    if (isHttps) attributes.push("Secure");
  } else {
    attributes.push("SameSite=Lax");
    if (isHttps) attributes.push("Secure");
  }

  return attributes.join("; ");
}

export function deleteCookieClientSide(name: CookieName): void {
  if (typeof document === "undefined") return;

  const hostname = window.location.hostname;
  const pathname = window.location.pathname || "/";

  const domains: Array<string | undefined> = [
    undefined,
    ...getDomainVariants(hostname),
  ];
  const paths: Array<string | undefined> = getPathVariants(pathname);
  const sameSiteVariants = [false, true];

  for (const domain of domains) {
    for (const path of paths) {
      for (const useNone of sameSiteVariants) {
        document.cookie = buildDeletionStrings(name, path, domain, useNone);
      }
    }
  }
}

export function clearCookiesClientSide(names?: CookieName[]): void {
  if (typeof document === "undefined") return;

  const cookieNames =
    names && names.length > 0
      ? names
      : document.cookie
          .split(";")
          .map((c) => c.trim())
          .filter(Boolean)
          .map((c) => c.split("=")[0]);

  Array.from(new Set(cookieNames)).forEach((name) =>
    deleteCookieClientSide(name),
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Clears stale Auth.js / net session cookies before a fresh sign-in.
 * Two API passes (JWT chunks first, then the rest) so Netlify Set-Cookie
 * limits cannot drop `__Secure-authjs.session-token.0` while clearing others.
 * Does not call Auth.js signOut — that requires CSRF which we keep.
 */
export async function clearAuthSessionBeforeSignIn(): Promise<void> {
  const jwtNames = expandSessionJwtCookieNamesClient();
  clearCookiesClientSide([...AUTH_COOKIE_NAMES_WITHOUT_CSRF]);
  await clearAuthCookiesViaApi(jwtNames);
  await clearAuthCookiesViaApi([...AUTH_COOKIE_NAMES_WITHOUT_CSRF]);
  await wait(SETTLE_MS);
}

/**
 * Before credentials sign-in — drop stale OAuth JWT + chunks only.
 * Keep CSRF. Net/bridge cookies are replaced by the server-action `/login`.
 */
export async function clearStaleAuthJsSessionOnly(): Promise<void> {
  const names = expandSessionJwtCookieNamesClient();
  clearCookiesClientSide(names);
  // Dedicated JWT-only pass (exact Auth.js Domain) — do not mix with net clears.
  await clearAuthCookiesViaApi(names);
  await wait(SETTLE_MS);
}
